import React, { useState, useEffect } from "react";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col } from "react-bootstrap";
import NewTableComponent from "../../../../components/NewTableComponent";
import CustomModal from "../../../../components/CustomModal";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import Input from "../../../../components/input";
import Select from "../../../../components/Select";
import Button from "../../../../components/button";
import MultiLineText from "../../../../components/MultiLineText";
import { apiService } from "../../../../services/apiService";
import { successNotify, errorNotify } from "../../../../utils/toast";

const ApprovalWorkflowTab = ({ addTrigger }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Roles list state
  const [roles, setRoles] = useState([]);

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    id: "",
    name: "",
    order: 1,
    stage: 1,
    approval_role_id: "",
    is_final_stage: false,
    type: "invoice",
    status: "active",
  });

  // Delete states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const workflowTypeOptions = [
    { value: "invoice", label: "Invoice" },
    { value: "pay_role", label: "PayRole" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const roleOptions = roles.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));

  const workflowValidationSchema = Yup.object().shape({
    name: Yup.string().required("Stage Name is required"),
    stage: Yup.number()
      .required("Stage Order Sequence is required")
      .min(1, "Stage Order Sequence must be at least 1"),
    approval_role_id: Yup.string().required("Role Required is required"),
    type: Yup.string().required("Workflow Type is required"),
    status: Yup.string().required("Status is required"),
  });

  // React to add trigger from parent
  useEffect(() => {
    if (addTrigger > 0) {
      handleOpenAdd();
    }
  }, [addTrigger]);

  // Fetch Roles list on mount for select dropdown from app-roles/dropdown
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiService.getRolesDropdown();
        const results = response?.data || [];
        setRoles(results);
      } catch (error) {
        console.error("Failed to fetch roles dropdown list:", error);
      }
    };
    fetchRoles();
  }, []);

  // Fetch paginated workflow stages
  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const response = await apiService.getWorkflowsPaginated({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters,
      });

      const listData = response?.data || {};
      const results = listData.results || listData.data || [];
      const totalItems = listData.meta?.totalItems || listData.total || results.length;
      const totalPages = listData.meta?.totalPages || 1;

      setApiRows(results);
      setMeta({ totalItems, totalPages });
    } catch (error) {
      setApiRows([]);
      setMeta({ totalItems: 0, totalPages: 1 });
      errorNotify(error || "Failed to fetch workflow stages list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, [currentPage, limit, searchText, searchFilters, refetchTrigger]);

  const handleOpenAdd = () => {
    setInitialFormValues({
      id: "",
      name: "",
      order: "",
      stage: "",
      approval_role_id: "",
      is_final_stage: false,
      type: "",
      status: "",
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setLoading(true);
      const res = await apiService.getWorkflowDetail(row.id);
      const detail = res?.data || {};
      setInitialFormValues({
        id: detail.id || "",
        name: detail.name || "",
        order: detail.order || 1,
        stage: detail.stage || 1,
        approval_role_id: detail.approval_role_id ? String(detail.approval_role_id) : "",
        is_final_stage: !!detail.is_final_stage,
        type: detail.type || "invoice",
        status: detail.status || "active",
      });
      setShowFormModal(true);
    } catch (error) {
      errorNotify(error || "Failed to fetch workflow details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: values.name,
        stage: Number(values.stage),
        approval_role_id: Number(values.approval_role_id),
        is_final_stage: values.is_final_stage,
        type: values.type,
        status: values.status,
      };

      if (values.id) {
        await apiService.updateWorkflow({
          financeWorkflow_id: Number(values.id),
          ...payload,
        });
        successNotify("Workflow stage updated successfully");
      } else {
        await apiService.createWorkflow(payload);
        successNotify("Workflow stage created successfully");
      }

      setShowFormModal(false);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to save workflow stage");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteWorkflow(deleteTargetId);
      successNotify("Workflow stage deleted successfully");
      setShowDeleteModal(false);
      setDeleteTargetId(null);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to delete workflow stage");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      name: "Workflow Type",
      key: "type",
      selector: (row) => row.type,
      cell: (row) => (
        <span
          className={`settings_badge ${row.type === "invoice"
            ? "settings_badge_invoice"
            : "settings_badge_payroll"
            }`}
        >
          {row.type === "invoice" ? "Invoice" : "PayRole"}
        </span>
      ),
      tableColumn: true,
      filterName: "typeFilter",
      type: "select",
      options: workflowTypeOptions,
      minWidth: "150px",
    },
    {
      name: "Stage Order Sequence",
      key: "stage",
      selector: (row) => row.stage,
      tableColumn: true,
      filterName: "stageFilter",
      type: "number",
      minWidth: "180px",
    },
    {
      name: "Stage Name",
      key: "name",
      selector: (row) => row.name || "",
      cell: (row) => (
        <MultiLineText text={row.name || "-"} maxWidth="150px" />
      ),
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Role Required",
      key: "approval_role_id",
      selector: (row) => {
        const found = roles.find((r) => String(r.id) === String(row.approval_role_id));
        return found ? found.name : row.approval_role?.name || "-";
      },
      cell: (row) => {
        const found = roles.find((r) => String(r.id) === String(row.approval_role_id));
        const displayName = found ? found.name : row.approval_role?.name || "-";
        return <MultiLineText text={displayName} maxWidth="150px" />;
      },
      tableColumn: true,
      filterName: "approvalRoleIdFilter",
      type: "select",
      options: roleOptions,
      minWidth: "180px",
    },
    {
      name: "Final Sign-Off",
      key: "is_final_stage",
      selector: (row) => (row.is_final_stage ? "Yes" : "No"),
      tableColumn: true,
      filterName: "isFinalStageFilter",
      type: "select",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      minWidth: "140px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={`settings_badge ${row.status === "active"
            ? "settings_badge_active"
            : "settings_badge_inactive"
            }`}
        >
          {row.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: statusOptions,
      minWidth: "130px",
    },
    {
      name: "Action",
      key: "action",
      cell: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="Edit stage"
            onClick={() => handleOpenEdit(row)}
          >
            <LuPencil />
          </button>
          <button
            type="button"
            className="delete_btn"
            title="Delete stage"
            onClick={() => handleOpenDelete(row.id)}
          >
            <RiDeleteBinLine />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="mt-2">
      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={apiRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={meta.totalItems}
        totalPages={meta.totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[10, 20, 50]}
        showFilter={false}
        showPagination={true}
      />

      {/* Form Modal */}
      <CustomModal
        show={showFormModal}
        handleClose={() => setShowFormModal(false)}
        heading={initialFormValues.id ? "Edit Stage" : "Add Stage"}
        size="lg"
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={workflowValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            submitForm,
          }) => (
            <div>
              <Row>
                <Col md={6}>
                  <Select
                    label="Workflow Type"
                    placeholder="Select Workflow Type"
                    options={workflowTypeOptions}
                    value={
                      workflowTypeOptions.find(
                        (opt) => opt.value === values.type
                      ) || null
                    }
                    name="type"
                    onChange={(option) =>
                      setFieldValue("type", option ? option.value : "invoice")
                    }
                    onBlur={handleBlur}
                    error={errors.type}
                    touched={touched.type}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Stage Name"
                    placeholder="e.g. Officer Review"
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.name}
                    touched={touched.name}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Input
                    label="Stage Order Sequence"
                    placeholder="Enter Stage Order Sequence"
                    type="number"
                    name="stage"
                    value={values.stage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.stage}
                    touched={touched.stage}
                    required
                  />
                </Col>

                <Col md={6}>
                  <Select
                    label="Approver Role Required"
                    placeholder="Select Approver Role"
                    options={roleOptions}
                    value={
                      roleOptions.find(
                        (opt) => opt.value === values.approval_role_id
                      ) || null
                    }
                    name="approval_role_id"
                    onChange={(option) =>
                      setFieldValue("approval_role_id", option ? option.value : "")
                    }
                    onBlur={handleBlur}
                    error={errors.approval_role_id}
                    touched={touched.approval_role_id}
                  />
                </Col>

                <Col md={6}>
                  <Select
                    label="Status"
                    placeholder="Select Status"
                    options={statusOptions}
                    value={
                      statusOptions.find((opt) => opt.value === values.status) ||
                      null
                    }
                    name="status"
                    onChange={(option) =>
                      setFieldValue("status", option ? option.value : "active")
                    }
                    onBlur={handleBlur}
                    error={errors.status}
                    touched={touched.status}
                  />
                </Col>

                <Col md={6} className="d-flex align-items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="isFinalStage"
                    checked={values.is_final_stage}
                    onChange={(e) => setFieldValue("is_final_stage", e.target.checked)}
                    style={{ width: "auto", cursor: "pointer" }}
                  />
                  <label htmlFor="isFinalStage" className="m-0 cursor-pointer text-sm font-semibold">
                    Final Stage
                  </label>
                </Col>

                <Col md={12}>
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      cancelBtn={() => setShowFormModal(false)}
                      submitBtn={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        submitForm();
                      }}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      submitText="Save"
                      type="button"
                      shouldRow={false}
                      buttonExtraCss="fuel_add_btn"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Formik>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Workflow Stage"
        description="Are you sure you want to delete this workflow stage? This action cannot be undone."
      />
    </div>
  );
};

export default ApprovalWorkflowTab;